import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Anchor,
  MapPin,
  Clock,
  Car,
  CheckCircle2,
  ChevronRight,
  Ship,
  Waves,
  Mountain,
  Building2,
  Church,
  Palette,
  Phone,
  Mail,
  ArrowRight,
  ArrowLeft
} from "lucide-react";

// Images for destinations
const IMAGES = {
  hero: "https://images.unsplash.com/photo-1668670579233-7f0b202f7028?auto=format&fit=crop&w=1920&q=80",
  etretat: "https://images.unsplash.com/photo-1668670579233-7f0b202f7028?auto=format&fit=crop&w=800&q=80",
  honfleur: "https://images.unsplash.com/photo-1659640861468-5a2308c31ddf?auto=format&fit=crop&w=800&q=80",
  leHavreBeach: "https://images.unsplash.com/photo-1586335612765-e2ade65cc004?auto=format&fit=crop&w=800&q=80",
  leHavreCity: "https://images.unsplash.com/photo-1728903174532-5b350eb5e951?auto=format&fit=crop&w=800&q=80",
};

const CLIENT_PHONE = "+33278780404";
const CLIENT_PHONE_DISPLAY = "+33 2 78 78 04 04";

const DestinationCard = ({ icon: Icon, title, distance, time, description, image, highlights }) => (
  <Card className="overflow-hidden hover:shadow-lg transition-shadow duration-300">
    {image && (
      <div className="relative h-48 overflow-hidden">
        <img 
          src={image} 
          alt={title}
          className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
          <div className="flex items-center gap-3 text-white text-sm">
            <span className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              {distance}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {time}
            </span>
          </div>
        </div>
      </div>
    )}
    <CardContent className="p-5">
      <div className="flex items-start gap-3 mb-3">
        <div className="p-2 bg-orange-100 rounded-lg shrink-0">
          <Icon className="w-5 h-5 text-orange-600" />
        </div>
        <div>
          <h3 className="font-bold text-lg text-slate-900">{title}</h3>
          {!image && (
            <p className="text-sm text-slate-500 flex items-center gap-2 mt-1">
              <MapPin className="w-3 h-3" /> {distance} | <Clock className="w-3 h-3" /> {time}
            </p>
          )}
        </div>
      </div>
      <p className="text-slate-600 text-sm leading-relaxed">{description}</p>
      {highlights && (
        <ul className="mt-3 space-y-1">
          {highlights.map((h, i) => (
            <li key={i} className="text-sm text-slate-600 flex items-center gap-2">
              <ChevronRight className="w-4 h-4 text-orange-500 shrink-0" />
              {h}
            </li>
          ))}
        </ul>
      )}
    </CardContent>
  </Card>
);

export default function ShoreExcursionsPage() {
  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Visit Ideas for Seafarers | Shore Services Le Havre</title>
        <meta name="description" content="Private VTC transfers from Le Havre port for seafarers. Discover Normandy's best destinations: Étretat cliffs, Honfleur harbor, UNESCO heritage sites. Book your transfer today." />
        <meta name="keywords" content="VTC port du Havre marins, shore transfer Le Havre seamen, transport marins escale Le Havre, Étretat depuis Le Havre port, Honfleur depuis port du Havre VTC, private transfer Le Havre port seafarers" />
        <link rel="canonical" href="https://shore.y-go.fr/excursions" />
        <meta property="og:title" content="Visit Ideas for Seafarers | Le Havre Port Transfers" />
        <meta property="og:description" content="Private VTC transfers from Le Havre port to Normandy's iconic destinations. Étretat cliffs, Honfleur harbor, UNESCO heritage sites." />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Simple Header with Return to Home */}
        <header className="bg-slate-900 text-white sticky top-0 z-50">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              {/* Back to Home */}
              <Link to="/" className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors">
                <ArrowLeft className="w-5 h-5" />
                <span className="text-sm font-medium">Return to Home</span>
              </Link>
              
              {/* Logo */}
              <Link to="/" className="flex items-center gap-2">
                <div className="p-2 bg-orange-500 rounded-lg">
                  <Anchor className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold">Y GO</span>
              </Link>
              
              {/* Book Now Button */}
              <Link to="/booking">
                <Button className="bg-orange-500 hover:bg-orange-600">
                  Book Now
                </Button>
              </Link>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="relative min-h-[60vh] flex items-center">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${IMAGES.hero})` }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-slate-900/50" />
          </div>
          
          <div className="relative container mx-auto px-4 py-16">
            <div className="max-w-3xl">
              <span className="inline-block px-4 py-2 bg-orange-500/20 text-orange-400 rounded-full text-sm font-semibold mb-6">
                Seamen's Shore Services
              </span>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Visit Ideas for Seafarers in Le Havre
              </h1>
              
              <p className="text-xl text-slate-300 mb-8 max-w-2xl">
                Private VTC transfers from Le Havre port to Normandy's most iconic destinations. 
                Make the most of your shore leave with comfortable, reliable transport.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/booking">
                  <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 text-lg">
                    <Ship className="w-5 h-5 mr-2" />
                    BOOK NOW
                  </Button>
                </Link>
                <a href={`tel:${CLIENT_PHONE}`}>
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                    <Phone className="w-5 h-5 mr-2" />
                    {CLIENT_PHONE_DISPLAY}
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Why Visit Section */}
        <section className="py-16 md:py-24 bg-slate-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8 text-center">
              Why Visit During Your Shore Leave?
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto text-center mb-12">
              Le Havre is the perfect gateway to Normandy's treasures. With its strategic location, 
              you can explore UNESCO World Heritage sites, stunning cliffs, and charming harbors 
              — all within a short drive from the port.
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: MapPin, title: "Gateway to Normandy", text: "Direct access to iconic Norman destinations" },
                { icon: Clock, title: "Short Travel Times", text: "Major sites reachable in 30-45 minutes" },
                { icon: Building2, title: "UNESCO Heritage", text: "Le Havre city center: World Heritage Site" },
                { icon: Car, title: "Port Expertise", text: "Drivers with terminal access knowledge" },
              ].map((item, i) => (
                <Card key={i} className="text-center p-6 hover:shadow-lg transition-shadow">
                  <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-7 h-7 text-orange-600" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-600">{item.text}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Le Havre Nearby */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 text-center">
              Le Havre: Places to Visit Near the Port
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto text-center mb-12">
              Discover the best of Le Havre within minutes of the port — perfect for short shore leave.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              <DestinationCard
                icon={Waves}
                title="Le Havre Beach & Waterfront"
                distance="~5 km"
                time="~15 min"
                image={IMAGES.leHavreBeach}
                description="Urban beach with stunning sunset views, seaside promenade, restaurants, and the iconic Le Havre waterfront. Perfect for a relaxing stroll during shore leave."
              />
              
              <DestinationCard
                icon={Building2}
                title="UNESCO City Center (Auguste Perret)"
                distance="~3 km"
                time="~10 min"
                image={IMAGES.leHavreCity}
                description="Le Havre's post-war city center rebuilt by architect Auguste Perret is a UNESCO World Heritage Site. Unique modernist architecture and urban planning."
              />
              
              <DestinationCard
                icon={Church}
                title="Saint-Joseph Church"
                distance="~4 km"
                time="~12 min"
                description="Iconic 107-meter tower designed by Auguste Perret. The interior features breathtaking colorful stained glass windows creating a unique atmosphere."
                highlights={["107m landmark tower", "Stunning stained glass", "Perret masterpiece"]}
              />
              
              <DestinationCard
                icon={Palette}
                title="MuMa - Modern Art Museum"
                distance="~4 km"
                time="~12 min"
                description="One of France's finest Impressionist collections outside Paris. Located facing the sea with works by Monet, Renoir, and Boudin."
                highlights={["Impressionist collection", "Sea-facing location", "Monet, Renoir, Boudin"]}
              />
            </div>
          </div>
        </section>

        {/* Major Destinations */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-slate-900 to-slate-800 text-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">
              Must-See Destinations from Le Havre Port
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto text-center mb-12">
              Half-day visits to Normandy's most iconic destinations — compatible with shore leave schedules.
            </p>
            
            <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {/* Étretat */}
              <div className="bg-white rounded-2xl overflow-hidden text-slate-900">
                <div className="relative h-64">
                  <img 
                    src={IMAGES.etretat}
                    alt="Étretat cliffs - Famous white chalk cliffs in Normandy"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute top-4 left-4 bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                    Most Popular
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 mb-4 text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" /> ~30 km
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" /> 40–45 min
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold mb-3 flex items-center gap-2">
                    <Mountain className="w-6 h-6 text-orange-500" />
                    Étretat Cliffs
                  </h3>
                  <p className="text-slate-600 mb-4">
                    The most iconic natural landmark of Normandy. These dramatic white chalk cliffs 
                    inspired artists from Monet to Maupassant. Perfect for a half-day visit.
                  </p>
                  <ul className="space-y-2 mb-6">
                    {["Aval & Amont Cliffs", "The Hollow Needle (Aiguille Creuse)", "Coastal walking paths", "Village restaurants & shops"].map((h, i) => (
                      <li key={i} className="flex items-center gap-2 text-slate-700">
                        <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" />
                        {h}
                      </li>
                    ))}
                  </ul>
                  <Link to="/booking">
                    <Button className="w-full bg-orange-500 hover:bg-orange-600">
                      Book Your Transfer
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
              
              {/* Honfleur */}
              <div className="bg-white rounded-2xl overflow-hidden text-slate-900">
                <div className="relative h-64">
                  <img 
                    src={IMAGES.honfleur}
                    alt="Honfleur old harbor - Picturesque Norman fishing port"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute top-4 left-4 bg-blue-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                    Charming Harbor
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 mb-4 text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" /> ~25 km
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" /> 35–40 min
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold mb-3 flex items-center gap-2">
                    <Anchor className="w-6 h-6 text-blue-500" />
                    Honfleur Old Harbor
                  </h3>
                  <p className="text-slate-600 mb-4">
                    One of the most picturesque harbors in France. This authentic Norman fishing port 
                    has charmed artists and visitors for centuries with its colorful facades.
                  </p>
                  <ul className="space-y-2 mb-6">
                    {["Vieux Bassin (Old Harbor)", "Sainte-Catherine wooden church", "Art galleries & boutiques", "Traditional Norman cuisine"].map((h, i) => (
                      <li key={i} className="flex items-center gap-2 text-slate-700">
                        <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" />
                        {h}
                      </li>
                    ))}
                  </ul>
                  <Link to="/booking">
                    <Button className="w-full bg-blue-500 hover:bg-blue-600">
                      Book Your Transfer
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* VTC Service Section */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8 text-center">
                Dedicated VTC Transfer Service for Seafarers
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6 mb-12">
                {[
                  { icon: MapPin, text: "Direct pickup at port terminal" },
                  { icon: Car, text: "Drivers with port access knowledge" },
                  { icon: CheckCircle2, text: "Comfortable, air-conditioned vehicles" },
                  { icon: Clock, text: "Schedules adapted to ship calls" },
                  { icon: Ship, text: "Flexible duration (few hours or full day)" },
                  { icon: Phone, text: "24/7 availability for port arrivals" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                    <div className="p-3 bg-orange-100 rounded-lg">
                      <item.icon className="w-6 h-6 text-orange-600" />
                    </div>
                    <span className="text-slate-700 font-medium">{item.text}</span>
                  </div>
                ))}
              </div>
              
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-8 text-center text-white">
                <h3 className="text-2xl font-bold mb-2">Private Transfer Service</h3>
                <p className="text-orange-100 mb-6">
                  VTC service for seafarers • Port transfers • Shore visits
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/booking">
                    <Button size="lg" className="bg-white text-orange-600 hover:bg-orange-50 px-8">
                      BOOK NOW
                    </Button>
                  </Link>
                  <a href={`tel:${CLIENT_PHONE}`}>
                    <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8">
                      <Phone className="w-5 h-5 mr-2" />
                      Call Us
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer CTA */}
        <section className="py-16 bg-slate-900 text-white">
          <div className="container mx-auto px-4 text-center">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="p-3 bg-orange-500 rounded-xl">
                <Anchor className="w-8 h-8 text-white" />
              </div>
            </div>
            <h2 className="text-3xl font-bold mb-2">Book Your Transfer Today</h2>
            <p className="text-xl text-slate-400 mb-2">Seamen's Shore Services – Le Havre</p>
            <p className="text-slate-500 mb-8">
              Private VTC transfers for seafarers
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link to="/booking">
                <Button size="lg" className="bg-orange-500 hover:bg-orange-600 px-10 py-6 text-lg">
                  BOOK NOW
                </Button>
              </Link>
            </div>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-slate-400">
              <a href={`tel:${CLIENT_PHONE}`} className="flex items-center gap-2 hover:text-orange-400 transition-colors">
                <Phone className="w-5 h-5" />
                {CLIENT_PHONE_DISPLAY}
              </a>
              <a href="mailto:booking@y-go.fr" className="flex items-center gap-2 hover:text-orange-400 transition-colors">
                <Mail className="w-5 h-5" />
                booking@y-go.fr
              </a>
            </div>
          </div>
        </section>

        {/* Back to Home Footer */}
        <div className="bg-slate-800 py-4">
          <div className="container mx-auto px-4 text-center">
            <Link to="/" className="text-slate-400 hover:text-white transition-colors text-sm flex items-center justify-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Return to Y GO Home
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
